import { useEffect } from 'react'
import { useStore } from '../store'
import { editorDate } from '../lib/format'

/** Plain text of a ProseMirror doc, for the preview line under each entry. */
function preview(node: unknown): string {
  if (!node || typeof node !== 'object') return ''
  const n = node as { type?: string; text?: string; content?: unknown[] }
  if (n.type === 'text') return n.text ?? ''
  const inner = (n.content ?? []).map(preview).filter(Boolean).join(' ')
  return inner
}

/**
 * Past revisions of the open note. Apple shows these in a sheet; a side panel
 * keeps the note itself visible, which is what you are comparing against.
 */
export function History() {
  const { historyFor, versions, closeHistory, restoreVersion } = useStore()

  useEffect(() => {
    if (!historyFor) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeHistory() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [historyFor, closeHistory])

  if (!historyFor) return null

  return (
    <aside className="history-panel" aria-label="Version history">
      <header className="history-head">
        <h2>Version History</h2>
        <button className="history-close" onClick={closeHistory} aria-label="Close history">✕</button>
      </header>

      {versions.length === 0 ? (
        <p className="history-empty">
          No earlier versions yet. Indigo Notes keeps a snapshot each time you come back to a note.
        </p>
      ) : (
        <ul className="history-list">
          {versions.map((v) => (
            <li key={v.id} className="history-item">
              <div className="history-when">{editorDate(v.savedAt)}</div>
              <div className="history-title">{v.title || 'New Note'}</div>
              <div className="history-preview">{preview(v.content).slice(0, 120)}</div>
              <button className="history-restore" onClick={() => restoreVersion(v.id)}>
                Restore
              </button>
            </li>
          ))}
        </ul>
      )}
    </aside>
  )
}
